import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import useContainerQuery from "../../../../hooks/useContainerQuery";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  getExpectedShops,
  seedDemoDetailedShipments,
  setSelectedStorageItemId,
} from "../../../../reduxFolder/reducers/storage";
import { authSelectUnits } from "../../../../reduxFolder/selectors/globalSelectors";
import { servicesSelectAddonsAll } from "../../../../reduxFolder/selectors/servicesSelectors";
import {
  selectedStorageItemId,
  storageSelectExpectedShops,
} from "../../../../reduxFolder/selectors/storageSelectors";
import StorageItemRow, {
  getLayoutTier,
  RowLayoutTier,
} from "../StorageList/StorageItemRow";
import { Wrapper } from "../StorageList/StorageList.styles";
import DemoAddressCard from "./DemoAddressCard";
import { demoDetailedShipments, demoStorageItems } from "./demoFixtures";
import DemoFrame from "./DemoFrame";
import { FadeWrapper } from "./DemoShipments.styles";

interface DemoShipmentsProps {
  units: string;
  addons: any;
  shops: any;
  selectedStorageItemId: number | null;
  openRightPanel: OpenRightPanel;
  setSelectedStorageItemId: (id: number | null) => void;
  seedDemoDetailedShipments: (shipments: any) => void;
  getExpectedShops: () => Promise<void>;
}

const noopSelect = () => {};
const noopSelectRange = () => List();

const DemoShipments: FC<DemoShipmentsProps> = ({
  units,
  addons,
  shops,
  selectedStorageItemId: selectedItemId,
  openRightPanel,
  setSelectedStorageItemId,
  seedDemoDetailedShipments,
  getExpectedShops,
}) => {
  const { t } = useTranslation("common");
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Drop row columns as the list narrows (viewport or right-panel squeeze),
  // matching the real StorageList.
  const layoutTier = useContainerQuery<RowLayoutTier>(
    wrapperRef,
    getLayoutTier,
    "full",
  );

  // Pre-seed the detail cache before any row can be clicked, so the panel
  // renders demo shipments without ever fetching.
  useEffect(() => {
    seedDemoDetailedShipments(demoDetailedShipments);
  }, [seedDemoDetailedShipments]);

  // Store logos: the expected-shipments shop catalog maps the fixtures'
  // store_name to an emblem (same lookup ExpectedItemRow uses). Fetched here
  // because Storage itself only loads the vendor-filter catalog, which can be
  // empty for a parcel-less account.
  useFetchOnceEffect(!shops.size, getExpectedShops);

  const parcels = useMemo(
    () =>
      demoStorageItems.map((parcel: any) => {
        const shop = shops.find(
          (x: any) => x.get("name") === parcel.get("store_name"),
        );
        const thumb = shop && shop.get("thumb_image_url");
        return thumb ? parcel.set("emblem_thumb_url", thumb) : parcel;
      }),
    [shops],
  );

  // isDemo makes StorageItemPanel browse-only: it skips its fetches and swaps
  // comment mutations for the demo hint, so no stub callbacks are needed here.
  const handleFocusStorageItemInPanel = useCallback(
    (parcel: any) => {
      setSelectedStorageItemId(parcel.get("id"));
      openRightPanel(
        "STORAGE_ITEM",
        Map({
          hasInventoryItems: parcel.get("items_quantity", 0) > 1,
          originalItem: parcel,
          isCartVariant: true,
          isDemo: true,
        }).merge(parcel),
      );
    },
    [openRightPanel, setSelectedStorageItemId],
  );

  return (
    <>
      <DemoFrame
        title={t("parcels.demo.title")}
        subtitle={t("parcels.demo.subtitle")}
      >
        <FadeWrapper>
          <Wrapper ref={wrapperRef}>
            {parcels.map((parcel: any) => (
              <StorageItemRow
                key={parcel.get("id")}
                parcel={parcel}
                selectedParcel={selectedItemId}
                onFocusStorageItemInPanel={handleFocusStorageItemInPanel}
                isChecked={false}
                disabled={false}
                units={units}
                addons={addons}
                layoutTier={layoutTier}
                handleSelect={noopSelect}
                selectedRangeParcels={noopSelectRange}
                isDemo
              />
            ))}
          </Wrapper>
        </FadeWrapper>
      </DemoFrame>
      <DemoAddressCard />
    </>
  );
};

const withConnect = connect(
  createStructuredSelector({
    units: authSelectUnits,
    addons: servicesSelectAddonsAll,
    shops: storageSelectExpectedShops,
    selectedStorageItemId,
  }),
  {
    openRightPanel,
    setSelectedStorageItemId,
    seedDemoDetailedShipments,
    getExpectedShops,
  },
);

export default withConnect(DemoShipments);
